import React, { useEffect, useState } from "react";
import { api } from "@/auth/AuthContext";
import { useAuth } from "@/auth/AuthContext";
import { useT } from "@/i18n/I18nContext";
import { PageHeader, Card, Stat } from "@/components/ui-kit";
import { useNavigate } from "react-router-dom";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Crown, Users, ChartBar, Receipt, Coins, Trash, Prohibit, ArrowCounterClockwise } from "@phosphor-icons/react";
import { StripeLiveKeyCard } from "@/components/StripeLiveKeyCard";
import { toast } from "sonner";

const Admin = () => {
  const { user } = useAuth();
  const { lang } = useT();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [txs, setTxs] = useState([]);
  const [tab, setTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (user && !user.is_admin) {
      navigate("/app", { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    (async () => {
      try {
        const [s, u, t] = await Promise.all([
          api.get("/admin/stats").then(r => r.data),
          api.get("/admin/users").then(r => r.data),
          api.get("/admin/transactions").then(r => r.data),
        ]);
        setStats(s);
        setUsers(u);
        setTxs(t);
      } catch (e) {
        setErr(e.response?.data?.detail || "Failed to load admin data");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <div className="p-8 text-zinc-500">Loading...</div>;
  if (err) return <div className="p-8 text-red-400">{err}</div>;

  const adminErr = (e) => toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Akce selhala");

  const changePlan = async (u, plan) => {
    try {
      await api.patch(`/admin/users/${u.id}/plan`, { plan });
      setUsers((prev) => prev.map((x) => x.id === u.id ? { ...x, plan, subscription_status: plan === "FREE" ? "none" : "admin_granted" } : x));
      toast.success(`${u.email} → ${plan}`);
    } catch (e) { adminErr(e); }
  };

  const toggleSuspend = async (u) => {
    try {
      const { data } = await api.patch(`/admin/users/${u.id}/suspend`, { suspended: !u.suspended });
      setUsers((prev) => prev.map((x) => x.id === u.id ? { ...x, suspended: data.suspended } : x));
      toast.success(data.suspended ? (lang === "cs" ? "Účet pozastaven" : "Account suspended") : (lang === "cs" ? "Účet obnoven" : "Account restored"));
    } catch (e) { adminErr(e); }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(lang === "cs" ? `Trvale smazat účet ${u.email} včetně všech dat? Tuto akci nelze vrátit.` : `Permanently delete ${u.email} and all their data?`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      setUsers((prev) => prev.filter((x) => x.id !== u.id));
      toast.success(lang === "cs" ? "Účet smazán" : "Account deleted");
    } catch (e) { adminErr(e); }
  };

  const subBadge = (s) => {
    if (s === "stripe_active") return ["Stripe", "bg-green-500/10 text-green-400 border-green-500/30"];
    if (s === "google_play") return ["Google Play", "bg-blue-500/10 text-blue-400 border-blue-500/30"];
    if (s === "admin_granted") return [lang === "cs" ? "Ruční" : "Manual", "bg-amber-500/10 text-amber-400 border-amber-500/30"];
    return ["—", "bg-zinc-800 text-zinc-500 border-zinc-700"];
  };

  return (
    <div>
      <PageHeader
        title={lang === "cs" ? "Admin Panel" : "Admin Panel"}
        sub={lang === "cs" ? "Přehled uživatelů, plateb a metrik" : "Users, payments and metrics overview"}
        action={
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md bg-amber-500/10 border border-amber-500/30 text-amber-400 text-xs font-mono uppercase tracking-wider">
            <Crown weight="fill" size={12} /> OWNER
          </span>
        }
      />
      <div className="p-4 sm:p-6 lg:p-8 space-y-6">
        {/* Tab bar */}
        <div className="flex gap-2 border-b border-zinc-800">
          {[
            ["overview", lang === "cs" ? "Přehled" : "Overview"],
            ["users", lang === "cs" ? `Uživatelé (${users.length})` : `Users (${users.length})`],
            ["transactions", lang === "cs" ? `Platby (${txs.length})` : `Transactions (${txs.length})`],
          ].map(([k, l]) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              data-testid={`admin-tab-${k}`}
              className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-[1px] ${tab === k ? "border-blue-500 text-blue-400" : "border-transparent text-zinc-500 hover:text-zinc-200"}`}
            >
              {l}
            </button>
          ))}
        </div>

        {tab === "overview" && (
          <>
            <StripeLiveKeyCard lang={lang} />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Stat label={lang === "cs" ? "Celkem uživatelů" : "Total users"} value={stats.total_users} accent="blue" testid="admin-total-users" />
              <Stat label={lang === "cs" ? "Platící" : "Paying users"} value={stats.paying_users} accent="green" testid="admin-paying" />
              <Stat label="MRR" value={`${stats.mrr_czk.toLocaleString("cs")} Kč`} accent="green" testid="admin-mrr" />
              <Stat label="ARR" value={`${stats.arr_czk.toLocaleString("cs")} Kč`} accent="blue" testid="admin-arr" />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Stat label="FREE" value={stats.users_by_plan.FREE} testid="admin-plan-free" />
              <Stat label="PREMIUM" value={stats.users_by_plan.PREMIUM} accent="blue" testid="admin-plan-premium" />
              <Stat label="ELITE" value={stats.users_by_plan.ELITE} accent="amber" testid="admin-plan-elite" />
              <Stat label={lang === "cs" ? "Celkový revenue" : "Total revenue"} value={`${stats.total_revenue_czk.toLocaleString("cs")} Kč`} accent="green" testid="admin-revenue" />
            </div>
            <Card className="p-6">
              <h3 className="text-lg font-medium text-zinc-100 mb-4">{lang === "cs" ? "Nové registrace (posledních 14 dní)" : "New signups (last 14 days)"}</h3>
              {stats.signups_last_14_days.length > 0 ? (
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={stats.signups_last_14_days}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                      <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#71717a" }} />
                      <YAxis tick={{ fontSize: 10, fill: "#71717a" }} />
                      <Tooltip contentStyle={{ background: "#09090b", border: "1px solid #27272a", borderRadius: 6, fontSize: 12 }} />
                      <Bar dataKey="count" fill="#e3b341" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : <div className="text-zinc-500 text-sm text-center py-8">{lang === "cs" ? "Zatím žádné registrace" : "No signups yet"}</div>}
            </Card>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="p-6">
                <div className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-2">{lang === "cs" ? "AI analýzy" : "AI analyses"}</div>
                <div className="text-3xl font-mono font-medium text-zinc-100">{stats.total_analyses}</div>
              </Card>
              <Card className="p-6">
                <div className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-2">{lang === "cs" ? "Zaznamenané obchody" : "Journal trades"}</div>
                <div className="text-3xl font-mono font-medium text-zinc-100">{stats.total_trades}</div>
              </Card>
              <Card className="p-6">
                <div className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-2">{lang === "cs" ? "Placené transakce" : "Paid transactions"}</div>
                <div className="text-3xl font-mono font-medium text-zinc-100">{stats.paid_transactions}</div>
              </Card>
            </div>
          </>
        )}

        {tab === "users" && (
          <Card className="overflow-hidden" data-testid="admin-users-table">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-zinc-900/50 border-b border-zinc-800 text-xs font-mono uppercase tracking-wider text-zinc-500">
                  <tr>
                    <th className="px-4 py-3 text-left">Email</th>
                    <th className="px-4 py-3 text-left">{lang === "cs" ? "Jméno" : "Name"}</th>
                    <th className="px-4 py-3 text-left">Plan</th>
                    <th className="px-4 py-3 text-left">{lang === "cs" ? "Předplatné" : "Subscription"}</th>
                    <th className="px-4 py-3 text-left">{lang === "cs" ? "Registrace" : "Signed up"}</th>
                    <th className="px-4 py-3 text-left">{lang === "cs" ? "Akce" : "Actions"}</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => {
                    const [subLabel, subCls] = subBadge(u.subscription_status);
                    return (
                    <tr key={u.id} className={`border-b border-zinc-900 hover:bg-zinc-900/30 ${u.suspended ? "opacity-50" : ""}`}>
                      <td className="px-4 py-3 font-mono text-zinc-200 text-sm">
                        <span className="inline-flex items-center gap-1.5">
                          {u.is_admin && <Crown weight="fill" size={13} className="text-amber-400 shrink-0" />}
                          {u.email}
                          {u.suspended && <span className="text-[10px] font-mono uppercase px-1.5 py-0.5 rounded bg-red-500/10 text-red-400 border border-red-500/30">{lang === "cs" ? "Pozastaven" : "Suspended"}</span>}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-zinc-300 text-sm">{u.full_name}</td>
                      <td className="px-4 py-3">
                        <select
                          value={u.plan === "PRO" ? "PREMIUM" : u.plan}
                          onChange={(e) => changePlan(u, e.target.value)}
                          disabled={u.is_admin}
                          data-testid={`admin-plan-select-${u.email}`}
                          className={`text-xs font-mono px-2 py-1 rounded border bg-zinc-950 outline-none cursor-pointer disabled:cursor-not-allowed ${
                            u.plan === "ELITE" ? "text-amber-400 border-amber-500/30" :
                            u.plan === "PREMIUM" || u.plan === "PRO" ? "text-blue-400 border-blue-500/30" :
                            "text-zinc-400 border-zinc-700"
                          }`}
                        >
                          <option value="FREE">FREE</option>
                          <option value="PREMIUM">PREMIUM</option>
                          <option value="ELITE">ELITE</option>
                        </select>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-mono px-2 py-0.5 rounded border ${subCls}`}>{subLabel}</span>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-zinc-500">{new Date(u.created_at).toLocaleString("cs-CZ")}</td>
                      <td className="px-4 py-3">
                        {!u.is_admin && (
                          <div className="flex items-center gap-1.5">
                            <button
                              onClick={() => toggleSuspend(u)}
                              data-testid={`admin-suspend-btn-${u.email}`}
                              title={u.suspended ? (lang === "cs" ? "Obnovit účet" : "Restore account") : (lang === "cs" ? "Pozastavit účet" : "Suspend account")}
                              className="p-1.5 rounded border border-zinc-800 text-zinc-400 hover:text-amber-400 hover:border-amber-500/40 transition-colors"
                            >
                              {u.suspended ? <ArrowCounterClockwise weight="bold" size={14} /> : <Prohibit weight="bold" size={14} />}
                            </button>
                            <button
                              onClick={() => deleteUser(u)}
                              data-testid={`admin-delete-btn-${u.email}`}
                              title={lang === "cs" ? "Trvale smazat" : "Delete permanently"}
                              className="p-1.5 rounded border border-zinc-800 text-zinc-400 hover:text-red-400 hover:border-red-500/40 transition-colors"
                            >
                              <Trash weight="bold" size={14} />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {tab === "transactions" && (
          <Card className="overflow-hidden" data-testid="admin-tx-table">
            {txs.length === 0 ? (
              <div className="p-12 text-center text-zinc-500 text-sm">{lang === "cs" ? "Zatím žádné transakce" : "No transactions yet"}</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-zinc-900/50 border-b border-zinc-800 text-xs font-mono uppercase tracking-wider text-zinc-500">
                    <tr>
                      <th className="px-4 py-3 text-left">Session</th>
                      <th className="px-4 py-3 text-left">Email</th>
                      <th className="px-4 py-3 text-left">Plan</th>
                      <th className="px-4 py-3 text-right">Amount</th>
                      <th className="px-4 py-3 text-left">Status</th>
                      <th className="px-4 py-3 text-left">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {txs.map((t) => (
                      <tr key={t.session_id} className="border-b border-zinc-900 hover:bg-zinc-900/30">
                        <td className="px-4 py-3 font-mono text-xs text-zinc-400">{t.session_id?.substring(0, 20)}…</td>
                        <td className="px-4 py-3 font-mono text-xs text-zinc-300">{t.user_email}</td>
                        <td className="px-4 py-3 text-sm text-zinc-100">{t.plan}</td>
                        <td className="px-4 py-3 font-mono text-sm text-zinc-100 text-right">{t.amount} {t.currency?.toUpperCase()}</td>
                        <td className="px-4 py-3">
                          <span className={`text-xs font-mono px-2 py-0.5 rounded border ${
                            t.payment_status === "paid" ? "bg-green-500/10 text-green-400 border-green-500/30" :
                            t.payment_status === "initiated" ? "bg-yellow-500/10 text-yellow-400 border-yellow-500/30" :
                            "bg-zinc-800 text-zinc-400 border-zinc-700"
                          }`}>{t.payment_status}</span>
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-zinc-500">{new Date(t.created_at).toLocaleString("cs-CZ")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        )}
      </div>
    </div>
  );
};

export default Admin;
